import { useShowMe } from '@entities/auth';
import { useListUserAiMessages, useSendUserAiMessage } from '@entities/user';
import { useOutgoingMessages } from '@packages/chat/react';
import { showNotification } from '@shared/utils';
import { isAxiosError } from 'axios';
import { useMemo } from 'react';
import { toLocalChatItem, toServerChatItem } from './lib/userSintiaChatItems';

const SEND_ERROR_MESSAGE = 'Не удалось отправить сообщение';

const getSendErrorMessage = (error: unknown) =>
  (isAxiosError<{ message?: string }>(error) &&
    error.response?.data?.message) ||
  SEND_ERROR_MESSAGE;

export const useUserSintiaChat = (
  userId: number,
  employerDisplayName: string
) => {
  const { data: me } = useShowMe();
  const history = useListUserAiMessages(userId);
  const mutation = useSendUserAiMessage(userId);
  const outgoing = useOutgoingMessages({
    sessionId: history.sessionId,
    canSend: true,
    successfulRefresh: history.successfulRefresh,
    getLatestStartedRequestId: history.getLatestStartedRequestId,
    send: mutation.mutateAsync,
    onSendFailed: (_clientId, error) =>
      showNotification({
        error,
        variant: 'error',
        message: getSendErrorMessage(error),
      }),
  });

  const items = useMemo(() => {
    const adminSender = me
      ? {
          id: me.id,
          firstName: me.profile.firstName,
          lastName: me.profile.lastName,
        }
      : null;

    return [
      ...outgoing.localMessages.map(message =>
        toLocalChatItem(message, adminSender)
      ),
      ...history.messages.map(message =>
        toServerChatItem(message, employerDisplayName)
      ),
    ];
  }, [outgoing.localMessages, history.messages, employerDisplayName, me]);

  return {
    items,
    history,
    sendMessage: outgoing.sendMessage,
    isSending: mutation.isPending,
    // Local rows are the newest: the user's own send moves the view.
    scrollToLatestKey: outgoing.localMessages.at(0)?.clientId,
  };
};

export type UserSintiaChat = ReturnType<typeof useUserSintiaChat>;
